/**
 * Audio de las partidas en Open Dahu: la música de fondo y los efectos.
 *
 * Todo pasa por Web Audio para que el volumen funcione igual en todos los
 * navegadores (en iPhone, `audio.volume` no hace nada). Cada jugador regula lo
 * suyo: las preferencias viven en su navegador y no afectan a nadie más.
 */

import { useEffect, useState } from 'react';

const MUSIC_URL = '/audio/trivia-theme.mp3';
/** La música va por debajo de los efectos aunque los dos estén al máximo. */
const MUSIC_HEADROOM = 0.55;

/* ---------------- preferencias del jugador ---------------- */

export interface AudioSettings {
  muted: boolean;
  /** 0..1 */
  music: number;
  /** 0..1 */
  sfx: number;
}

const SETTINGS_KEY = 'dahu_audio';
const DEFAULT_SETTINGS: AudioSettings = { muted: false, music: 0.6, sfx: 0.8 };

function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    /* sin almacenamiento: valores por defecto */
  }
  return { ...DEFAULT_SETTINGS };
}

let settings = loadSettings();
const listeners = new Set<(s: AudioSettings) => void>();

export function getAudioSettings(): AudioSettings {
  return settings;
}

export function setAudioSettings(patch: Partial<AudioSettings>) {
  settings = { ...settings, ...patch };
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* valdrá mientras no se recargue */
  }
  applyVolumes();
  listeners.forEach((fn) => fn(settings));
}

export function useAudioSettings(): AudioSettings {
  const [s, setS] = useState(settings);
  useEffect(() => {
    listeners.add(setS);
    return () => {
      listeners.delete(setS);
    };
  }, []);
  return s;
}

/* ---------------- grafo de audio ---------------- */

let audioCtx: AudioContext | null = null;
/** Volumen de efectos del jugador. */
let sfxBus: GainNode | null = null;
/** Entrada seca de los efectos y envío a la reverb (la "sala" que les da cuerpo). */
let sfxDry: GainNode | null = null;
let sfxWet: GainNode | null = null;
let musicBus: GainNode | null = null;
let musicEl: HTMLAudioElement | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return null;
    const ctx: AudioContext = new AudioContextClass();
    audioCtx = ctx;

    // Efectos → compresor suave → salida: varios sonidos a la vez no saturan
    // y los golpes graves no se comen a los agudos.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 12;
    comp.ratio.value = 4;
    comp.attack.value = 0.003;
    comp.release.value = 0.2;
    comp.connect(ctx.destination);

    sfxBus = ctx.createGain();
    sfxBus.connect(comp);
    sfxDry = ctx.createGain();
    sfxDry.connect(sfxBus);
    sfxWet = ctx.createGain();
    const reverb = ctx.createConvolver();
    reverb.buffer = makeImpulse(ctx, 1.8, 3);
    const reverbLevel = ctx.createGain();
    reverbLevel.gain.value = 0.35;
    sfxWet.connect(reverb);
    reverb.connect(reverbLevel);
    reverbLevel.connect(sfxBus);

    musicBus = ctx.createGain();
    musicBus.connect(ctx.destination);
    applyVolumes();
    void loadSamples(ctx);
  }
  if (audioCtx.state === 'suspended') void audioCtx.resume().catch(() => {});
  return audioCtx;
}

/** Respuesta de una sala pequeña, generada: ruido estéreo que se apaga. */
function makeImpulse(ctx: AudioContext, seconds: number, decay: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const ir = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return ir;
}

/* ---------------- muestras opcionales ---------------- */

/**
 * Si existe `/audio/sfx/<nombre>.mp3`, se usa ese archivo en lugar del sonido
 * sintetizado. Así se pueden poner efectos grabados sin tocar el código.
 */
type SampleName = 'tick' | 'select' | 'reveal' | 'correct' | 'wrong' | 'fanfare';
const SAMPLE_NAMES: SampleName[] = ['tick', 'select', 'reveal', 'correct', 'wrong', 'fanfare'];
const samples = new Map<SampleName, AudioBuffer>();

async function loadSamples(ctx: AudioContext) {
  await Promise.all(
    SAMPLE_NAMES.map(async (name) => {
      try {
        const res = await fetch(`/audio/sfx/${name}.mp3`);
        // Sin el archivo, el servidor puede devolver la página: solo vale audio.
        if (!res.ok || !res.headers.get('content-type')?.startsWith('audio')) return;
        samples.set(name, await ctx.decodeAudioData(await res.arrayBuffer()));
      } catch {
        /* se queda el sintetizado */
      }
    }),
  );
}

function playSample(name: SampleName, gain = 1): boolean {
  const buffer = samples.get(name);
  if (!buffer || !audioCtx || !sfxDry) return false;
  const src = audioCtx.createBufferSource();
  src.buffer = buffer;
  const g = audioCtx.createGain();
  g.gain.value = gain;
  src.connect(g);
  g.connect(sfxDry);
  src.start();
  return true;
}

function musicLevel() {
  return settings.muted ? 0 : settings.music * MUSIC_HEADROOM;
}

function applyVolumes() {
  if (!audioCtx) return;
  const now = audioCtx.currentTime;
  sfxBus?.gain.setTargetAtTime(settings.muted ? 0 : settings.sfx, now, 0.02);
  // Si la música se está apagando, no se le vuelve a subir el volumen.
  if (musicWanted) musicBus?.gain.setTargetAtTime(musicLevel(), now, 0.02);
}

/* ---------------- música ---------------- */

/** Si la partida quiere música ahora mismo (aunque el navegador aún no la deje sonar). */
let musicWanted = false;
let stopTimer: ReturnType<typeof setTimeout> | null = null;
let waitingGesture = false;

function getMusicElement(): HTMLAudioElement | null {
  const ctx = getAudioContext();
  if (!ctx || !musicBus) return null;
  if (!musicEl) {
    musicEl = new Audio(MUSIC_URL);
    musicEl.loop = true;
    musicEl.preload = 'auto';
    ctx.createMediaElementSource(musicEl).connect(musicBus);
  }
  return musicEl;
}

/** Sin un toque previo, el navegador no deja sonar nada: se reintenta en el primero. */
function retryOnGesture() {
  if (waitingGesture) return;
  waitingGesture = true;
  const retry = () => {
    waitingGesture = false;
    window.removeEventListener('pointerdown', retry);
    window.removeEventListener('keydown', retry);
    getAudioContext();
    if (musicWanted) void musicEl?.play().catch(() => {});
  };
  window.addEventListener('pointerdown', retry);
  window.addEventListener('keydown', retry);
}

/**
 * Pone la música. Con `restart` vuelve al principio (cada pregunta nueva);
 * sin él, si ya sonaba, sigue donde iba.
 */
export function startMusic({ restart = false } = {}) {
  try {
    const el = getMusicElement();
    if (!el || !audioCtx || !musicBus) return;
    if (stopTimer) {
      clearTimeout(stopTimer);
      stopTimer = null;
    }
    const alreadyPlaying = musicWanted && !el.paused;
    musicWanted = true;
    if (restart || !alreadyPlaying) {
      if (restart) el.currentTime = 0;
      musicBus.gain.cancelScheduledValues(audioCtx.currentTime);
      musicBus.gain.setValueAtTime(musicLevel(), audioCtx.currentTime);
    }
    // Si la carga falló (red caída), se vuelve a pedir en vez de quedar mudo.
    if (el.error) el.load();
    if (el.paused || el.error) {
      el.play().catch(retryOnGesture);
    }
    if (audioCtx.state === 'suspended') retryOnGesture();
  } catch {
    /* sin audio */
  }
}

/** Corta la música, con un fundido corto para que no suene a error. */
export function stopMusic(fadeMs = 120) {
  musicWanted = false;
  if (!musicEl || !audioCtx || !musicBus) return;
  const now = audioCtx.currentTime;
  musicBus.gain.cancelScheduledValues(now);
  musicBus.gain.setValueAtTime(musicBus.gain.value, now);
  musicBus.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
  if (stopTimer) clearTimeout(stopTimer);
  stopTimer = setTimeout(() => {
    stopTimer = null;
    if (!musicWanted) musicEl?.pause();
  }, fadeMs + 30);
}

/* ---------------- efectos ---------------- */

/*
 * Cada efecto se construye por capas (cuerpo, brillo, golpe) con envolventes
 * suaves, filtros y algo de estéreo, y pasa por la reverb y el compresor de
 * arriba. Así suenan a producción y no a pitido de ordenador.
 */

interface Out {
  ctx: AudioContext;
  dry: AudioNode;
  wet: AudioNode;
}

interface VoiceOpts {
  type?: OscillatorType;
  freq: number;
  /** Frecuencia final (barrido exponencial durante `glide` o toda la nota). */
  to?: number;
  glide?: number;
  start?: number;
  dur: number;
  gain?: number;
  attack?: number;
  /** Desafinaciones en cents: varias voces juntas suenan más anchas. */
  detune?: number[];
  filter?: { type?: BiquadFilterType; freq: number; to?: number; q?: number };
  pan?: number;
  /** Cuánto va a la reverb (0..1). */
  wet?: number;
}

/** Panorama, salida seca y envío a la reverb de una capa. */
function route(o: Out, node: AudioNode, pan = 0, wet = 0) {
  const p = o.ctx.createStereoPanner();
  p.pan.value = pan;
  node.connect(p);
  p.connect(o.dry);
  if (wet) {
    const send = o.ctx.createGain();
    send.gain.value = wet;
    p.connect(send);
    send.connect(o.wet);
  }
}

function envelope(ctx: AudioContext, t: number, attack: number, peak: number, end: number) {
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(peak, t + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, end);
  return env;
}

function voice(o: Out, v: VoiceOpts) {
  const { ctx } = o;
  const t = ctx.currentTime + (v.start ?? 0);
  const end = t + v.dur;
  const env = envelope(ctx, t, v.attack ?? 0.005, v.gain ?? 0.1, end);

  let node: AudioNode = env;
  if (v.filter) {
    const f = ctx.createBiquadFilter();
    f.type = v.filter.type ?? 'lowpass';
    f.Q.value = v.filter.q ?? 0.7;
    f.frequency.setValueAtTime(v.filter.freq, t);
    if (v.filter.to) f.frequency.exponentialRampToValueAtTime(v.filter.to, end);
    env.connect(f);
    node = f;
  }
  route(o, node, v.pan, v.wet);

  const detunes = v.detune ?? [0];
  detunes.forEach((cents) => {
    const osc = ctx.createOscillator();
    osc.type = v.type ?? 'sine';
    osc.detune.value = cents;
    osc.frequency.setValueAtTime(v.freq, t);
    if (v.to) osc.frequency.exponentialRampToValueAtTime(v.to, t + (v.glide ?? v.dur));
    const g = ctx.createGain();
    g.gain.value = 1 / Math.sqrt(detunes.length);
    osc.connect(g);
    g.connect(env);
    osc.start(t);
    osc.stop(end + 0.05);
  });
}

/** Campana FM: una moduladora en proporción no entera da ese brillo metálico. */
function bell(
  o: Out,
  { freq, start = 0, dur = 1.2, gain = 0.1, ratio = 3.5, index = 2.5, pan = 0, wet = 0.5 }: {
    freq: number; start?: number; dur?: number; gain?: number; ratio?: number; index?: number; pan?: number; wet?: number;
  },
) {
  const { ctx } = o;
  const t = ctx.currentTime + start;
  const carrier = ctx.createOscillator();
  const mod = ctx.createOscillator();
  const modGain = ctx.createGain();
  carrier.frequency.value = freq;
  mod.frequency.value = freq * ratio;
  // El brillo se apaga antes que la nota, como en una campana real.
  modGain.gain.setValueAtTime(freq * index, t);
  modGain.gain.exponentialRampToValueAtTime(freq * 0.05, t + dur * 0.6);
  mod.connect(modGain);
  modGain.connect(carrier.frequency);

  const env = envelope(ctx, t, 0.004, gain, t + dur);
  carrier.connect(env);
  route(o, env, pan, wet);

  carrier.start(t);
  mod.start(t);
  carrier.stop(t + dur + 0.05);
  mod.stop(t + dur + 0.05);
}

let noiseBuffer: AudioBuffer | null = null;
function getNoise(ctx: AudioContext) {
  if (!noiseBuffer) {
    noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = noiseBuffer.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
}

/** Ruido filtrado: platillos, golpes de aire, chasquidos. */
function noiseHit(
  o: Out,
  { start = 0, dur = 0.4, gain = 0.1, attack = 0.002, type = 'bandpass', freq = 3000, to, q = 0.8, pan = 0, wet = 0.3 }: {
    start?: number; dur?: number; gain?: number; attack?: number; type?: BiquadFilterType; freq?: number; to?: number; q?: number; pan?: number; wet?: number;
  },
) {
  const { ctx } = o;
  const t = ctx.currentTime + start;
  const src = ctx.createBufferSource();
  src.buffer = getNoise(ctx);
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.Q.value = q;
  f.frequency.setValueAtTime(freq, t);
  if (to) f.frequency.exponentialRampToValueAtTime(to, t + dur);
  const env = envelope(ctx, t, attack, gain, t + dur);
  src.connect(f);
  f.connect(env);
  route(o, env, pan, wet);
  src.start(t, Math.random());
  src.stop(t + dur + 0.05);
}

function withAudio(name: SampleName, play: (o: Out) => void) {
  try {
    if (settings.muted || settings.sfx === 0) return;
    const ctx = getAudioContext();
    if (!ctx || !sfxDry || !sfxWet) return;
    if (playSample(name)) return;
    play({ ctx, dry: sfxDry, wet: sfxWet });
  } catch {
    /* sin audio */
  }
}

let tickSide = 1;

/** Tic-tac de los últimos segundos; `urgency` 0..1 lo hace más agudo y fuerte. */
export function playTickSound(urgency = 0) {
  withAudio('tick', (o) => {
    tickSide = -tickSide;
    const pan = tickSide * 0.35;
    const f = 1500 + urgency * 700;
    // Bloque de madera: tono corto que cae + chasquido.
    voice(o, { type: 'sine', freq: f, to: f * 0.6, dur: 0.07, gain: 0.35 + urgency * 0.2, pan, wet: 0.15 });
    voice(o, { type: 'triangle', freq: f * 2.3, dur: 0.03, gain: 0.05, pan });
    noiseHit(o, { dur: 0.025, gain: 0.2, freq: 4500, q: 2, pan, wet: 0.1 });
    // En el último segundo, un latido grave que anuncia el corte.
    if (urgency >= 1) voice(o, { type: 'sine', freq: 90, to: 55, dur: 0.25, gain: 0.25 });
  });
}

/** Al elegir una respuesta: un "pop" redondo. */
export function playSelectSound() {
  withAudio('select', (o) => {
    voice(o, { type: 'sine', freq: 380, to: 920, glide: 0.06, dur: 0.16, gain: 0.45, wet: 0.2 });
    voice(o, { type: 'triangle', freq: 760, to: 1840, glide: 0.06, dur: 0.1, gain: 0.1, wet: 0.2 });
    noiseHit(o, { dur: 0.02, gain: 0.05, freq: 2500, q: 1.5 });
  });
}

/**
 * Golpe al cerrar la pregunta, como el de Kahoot: se corta la música con un
 * impacto grave, un golpe de aire y una campana que queda sonando.
 */
export function playRevealSting() {
  withAudio('reveal', (o) => {
    // Sub y cuerpo del impacto.
    voice(o, { type: 'sine', freq: 120, to: 38, glide: 0.5, dur: 0.9, gain: 0.5, wet: 0.2 });
    voice(o, { type: 'triangle', freq: 240, to: 90, glide: 0.2, dur: 0.35, gain: 0.12, filter: { freq: 1200 } });
    // Aire: barrido de ruido de agudo a grave, abierto en estéreo.
    noiseHit(o, { dur: 0.6, gain: 0.18, type: 'lowpass', freq: 9000, to: 300, q: 0.5, wet: 0.5 });
    noiseHit(o, { dur: 0.9, gain: 0.05, type: 'highpass', freq: 6000, attack: 0.01, pan: -0.3, wet: 0.6 });
    noiseHit(o, { dur: 0.9, gain: 0.05, type: 'highpass', freq: 7000, attack: 0.01, pan: 0.3, wet: 0.6 });
    // Campana que queda resonando en la sala.
    bell(o, { freq: 196, dur: 1.6, gain: 0.07, ratio: 2.76, index: 1.8, wet: 0.8 });
  });
}

/** Acierto: arpegio de campanas que sube y acorde brillante con destello. */
export function playCorrectSound() {
  withAudio('correct', (o) => {
    const notes = [523.25, 659.25, 783.99, 1046.5]; // Do5 Mi5 Sol5 Do6
    notes.forEach((freq, i) => {
      const pan = -0.45 + i * 0.3;
      bell(o, { freq, start: i * 0.075, dur: 0.6, gain: 0.11, ratio: 2, index: 1.2, pan, wet: 0.45 });
      voice(o, { type: 'triangle', freq, start: i * 0.075, dur: 0.3, gain: 0.07, pan, wet: 0.3 });
    });
    // Acorde final ancho (Do mayor con novena) y destello de agudos.
    [1046.5, 1318.5, 1568, 2349.3].forEach((freq, i) =>
      voice(o, {
        type: 'triangle', freq, start: 0.3, dur: 1.1, gain: 0.055, attack: 0.01,
        detune: [-6, 6], pan: (i % 2 ? 1 : -1) * 0.4, wet: 0.6,
        filter: { freq: 6000, to: 2500 },
      }),
    );
    noiseHit(o, { start: 0.3, dur: 0.8, gain: 0.04, type: 'highpass', freq: 8000, attack: 0.02, wet: 0.7 });
  });
}

/** Fallo: dos notas graves que caen, cálidas, sin chirriar. */
export function playWrongSound() {
  withAudio('wrong', (o) => {
    const brass = (freq: number, to: number, start: number, dur: number) =>
      voice(o, {
        type: 'sawtooth', freq, to, start, dur, gain: 0.16, attack: 0.02,
        detune: [-10, 0, 10], filter: { freq: 1400, to: 350, q: 1.2 }, wet: 0.25,
      });
    brass(311.1, 293.7, 0, 0.26); // Mib → Re
    brass(277.2, 207.7, 0.27, 0.6); // Do# que se hunde
    voice(o, { type: 'sine', freq: 138.6, to: 103.8, start: 0.27, dur: 0.6, gain: 0.18 });
  });
}

/** Podio completo: metales en tres acordes, timbal y platillo. */
export function playFanfareSound() {
  withAudio('fanfare', (o) => {
    const chords = [
      { notes: [392, 493.88, 587.33], time: 0, dur: 0.22 }, // Sol
      { notes: [392, 493.88, 587.33], time: 0.24, dur: 0.16 },
      { notes: [440, 554.37, 659.25], time: 0.42, dur: 0.22 }, // La
      { notes: [523.25, 659.25, 783.99, 1046.5], time: 0.68, dur: 1.5 }, // Do
    ];
    chords.forEach((c) =>
      c.notes.forEach((freq, i) =>
        voice(o, {
          type: 'sawtooth', freq, start: c.time, dur: c.dur, gain: 0.07, attack: 0.03,
          detune: [-8, 0, 8], pan: (i - (c.notes.length - 1) / 2) * 0.3, wet: 0.4,
          // El filtro se abre al atacar: así "sopla" como un metal.
          filter: { freq: 800, to: 3500, q: 1 },
        }),
      ),
    );
    [0, 0.42, 0.68].forEach((time) =>
      voice(o, { type: 'sine', freq: 110, to: 70, start: time, dur: 0.5, gain: 0.35, wet: 0.3 }),
    );
    noiseHit(o, { start: 0.68, dur: 1.8, gain: 0.09, type: 'highpass', freq: 5000, attack: 0.005, wet: 0.6 });
  });
}
